import { useState, type FormEvent } from "react";
import { api, setToken } from "./api";

type Props = { onAuthenticated: () => Promise<void> };
type Mode = "login" | "register" | "forgot" | "reset";

function initialMode(inviteToken: string | null, resetToken: string | null): Mode {
  if (resetToken) return "reset";
  if (inviteToken) return "register";
  return "login";
}

export function Login({ onAuthenticated }: Props) {
  const [inviteToken] = useState(
    () => new URLSearchParams(window.location.search).get("invite"),
  );
  const [resetToken] = useState(
    () => new URLSearchParams(window.location.search).get("reset"),
  );
  const [mode, setMode] = useState<Mode>(() => initialMode(inviteToken, resetToken));
  const isRegistering = mode === "register";
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // New restaurant fields
  const [restaurantName, setRestaurantName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
    setPassword("");
    setConfirmPassword("");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === "forgot") {
        await api.forgotPassword(email);
        // Same message for known and unknown addresses — the API never reveals
        // whether an account exists.
        setNotice("If an account exists for this address, a reset link is on its way.");
        return;
      }

      if (mode === "reset") {
        if (!resetToken) {
          throw new Error("This reset link is missing its token.");
        }
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match.");
        }
        await api.resetPassword(resetToken, password);
        const url = new URL(window.location.href);
        url.searchParams.delete("reset");
        window.history.replaceState({}, "", url.toString());
        switchMode("login");
        setNotice("Password updated. Sign in with your new password.");
        return;
      }

      if (isRegistering) {
        const { access_token } = await api.signup(
          email,
          password,
          fullName,
          inviteToken
            ? undefined
            : {
                name: restaurantName,
                legal_name: legalName,
                tax_id: taxId,
                address,
                phone,
              },
          inviteToken ?? undefined,
        );
        setToken(access_token);
      } else {
        const { access_token } = await api.login(email, password);
        setToken(access_token);
        if (inviteToken) {
          try {
            await api.acceptInvitation(inviteToken);
          } catch (err) {
            // Keep the session consistent: an unaccepted invite must not
            // leave the visitor signed in as the wrong account.
            setToken(null);
            throw err;
          }
        }
      }
      if (inviteToken) {
        const url = new URL(window.location.href);
        url.searchParams.delete("invite");
        window.history.replaceState({}, "", url.toString());
      }
      await onAuthenticated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const title = (() => {
    if (mode === "reset") return "Choose a new password";
    if (mode === "forgot") return "Reset your password";
    if (inviteToken) {
      return isRegistering
        ? "Create an account to join the team"
        : "Sign in to accept the invitation";
    }
    return isRegistering ? "Create your account" : "Sign in to your dashboard";
  })();

  const submitLabel = (() => {
    if (submitting) {
      if (mode === "forgot") return "Sending…";
      if (mode === "reset") return "Updating password…";
      return isRegistering ? "Creating account…" : "Signing in…";
    }
    if (mode === "forgot") return "Send reset link";
    if (mode === "reset") return "Set new password";
    return isRegistering ? "Create Account" : "Sign in";
  })();

  return (
    <div className="app">
      <div className="login-card-wrapper">
        <div style={{ width: "100%", maxWidth: 420 }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <h1 style={{ margin: 0, fontSize: 36, fontWeight: 600, background: "var(--accent-gradient)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: "-0.02em" }}>RESTIFY</h1>
            <p className="muted" style={{ marginTop: 8, fontSize: 16 }}>
              {title}
            </p>
          </div>
          
          <div className="card" style={{ marginTop: 0 }}>
            {inviteToken && mode !== "forgot" && mode !== "reset" && (
              <div
                style={{
                  marginBottom: 20,
                  padding: 14,
                  borderRadius: 10,
                  border: "1px solid rgba(99,102,241,0.3)",
                  background: "rgba(99,102,241,0.08)",
                  fontSize: 14,
                }}
              >
                You've been invited to join a restaurant on RESTIFY. Use the email
                address the invitation was sent to — other addresses are rejected.
              </div>
            )}
            {mode === "forgot" && (
              <p className="muted" style={{ marginTop: 0, marginBottom: 20, fontSize: 14 }}>
                Enter your account email and we'll send you a link to set a new
                password. The link expires after one hour.
              </p>
            )}
            <form onSubmit={submit}>
              {isRegistering && !inviteToken && (
                <>
                  <label className="label" htmlFor="fullName">
                    Full Name
                  </label>
                  <input
                    id="fullName"
                    className="input"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    style={{ marginBottom: 16 }}
                  />

                  <div style={{ padding: "16px 0", margin: "16px 0", borderTop: "1px solid rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <p style={{ margin: "0 0 16px 0", fontSize: 14, fontWeight: 600, color: "var(--accent-text)" }}>RESTAURANT DETAILS</p>
                    
                    <label className="label" htmlFor="restaurantName">Restaurant Name</label>
                    <input
                      id="restaurantName"
                      className="input"
                      type="text"
                      placeholder="e.g. Tasty Pizza"
                      value={restaurantName}
                      onChange={(e) => setRestaurantName(e.target.value)}
                      required
                      style={{ marginBottom: 12 }}
                    />

                    <label className="label" htmlFor="legalName">Legal Entity Name</label>
                    <input
                      id="legalName"
                      className="input"
                      type="text"
                      placeholder="e.g. Tasty Foods Ltd."
                      value={legalName}
                      onChange={(e) => setLegalName(e.target.value)}
                      required
                      style={{ marginBottom: 12 }}
                    />

                    <label className="label" htmlFor="taxId">Tax ID / NIP</label>
                    <input
                      id="taxId"
                      className="input"
                      type="text"
                      placeholder="e.g. 1234567890"
                      value={taxId}
                      onChange={(e) => setTaxId(e.target.value)}
                      required
                      style={{ marginBottom: 12 }}
                    />

                    <label className="label" htmlFor="address">Full Address</label>
                    <input
                      id="address"
                      className="input"
                      type="text"
                      placeholder="Street, City, Postcode"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      required
                      style={{ marginBottom: 12 }}
                    />

                    <label className="label" htmlFor="phone">Phone Number</label>
                    <input
                      id="phone"
                      className="input"
                      type="tel"
                      placeholder="+48 123 456 789"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </div>
                </>
              )}
              {mode !== "reset" && (
                <>
                  <label className="label" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    className="input"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{ marginBottom: 16 }}
                  />
                </>
              )}
              {mode !== "forgot" && (
                <>
                  <label className="label" htmlFor="password">
                    {mode === "reset" ? "New password" : "Password"}
                  </label>
                  <input
                    id="password"
                    className="input"
                    type="password"
                    autoComplete={mode === "reset" ? "new-password" : "current-password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={mode === "reset" ? 10 : undefined}
                    style={{ marginBottom: mode === "reset" ? 12 : 16 }}
                  />
                </>
              )}
              {mode === "reset" && (
                <>
                  <label className="label" htmlFor="confirmPassword">
                    Confirm new password
                  </label>
                  <input
                    id="confirmPassword"
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={10}
                    style={{ marginBottom: 16 }}
                  />
                </>
              )}
              {mode === "login" && !inviteToken && (
                <div style={{ textAlign: "right", marginTop: -8 }}>
                  <button
                    type="button"
                    className="btn-link"
                    style={{ padding: "4px 0" }}
                    onClick={() => switchMode("forgot")}
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              {notice && (
                <p style={{ color: "var(--success)", fontSize: 14, margin: "8px 0 0" }}>
                  {notice}
                </p>
              )}
              {error && <p className="error">{error}</p>}
              <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 16 }}>
                <button className="btn btn-primary" style={{ width: "100%", padding: "12px" }} type="submit" disabled={submitting}>
                  {submitLabel}
                </button>
                {(mode === "login" || mode === "register") && (
                  <button 
                    type="button"
                    className="btn btn-secondary" 
                    style={{ width: "100%", padding: "12px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)" }}
                    onClick={() => switchMode(isRegistering ? "login" : "register")}
                  >
                    {isRegistering ? "Already have an account? Sign in" : "Don't have an account? Create one"}
                  </button>
                )}
                {(mode === "forgot" || mode === "reset") && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: "100%", padding: "12px", background: "transparent", border: "1px solid rgba(255,255,255,0.1)" }}
                    onClick={() => switchMode("login")}
                  >
                    Back to sign in
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
